import { DollarSign, Clock, Eye, SkipForward, FileText } from 'lucide-react';

const typeColors = {
  'Floor Plan': 'text-blue-400',
  'Room Schedule': 'text-emerald-400',
  'Equipment Schedule': 'text-violet-400',
  'MEP Plan': 'text-primary',
  'Cover Sheet': 'text-muted-foreground',
  'Detail / Section': 'text-muted-foreground',
  'General Notes': 'text-muted-foreground',
};

export default function CostEstimate({ estimate, onConfirm, confirming }) {
  if (!estimate) return null;

  return (
    <div className="bg-card border border-border rounded-sm animate-fade-in" data-testid="cost-estimate">
      {/* Header */}
      <div className="px-5 py-3 border-b border-border flex items-center gap-2">
        <DollarSign className="w-4 h-4 text-amber-400" />
        <span className="font-heading font-bold text-xs uppercase tracking-wider text-foreground">Processing Estimate</span>
      </div>

      {/* Summary */}
      <div className="px-5 py-4">
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="bg-card border border-border rounded-sm p-3 text-center">
            <p className="font-heading text-[9px] uppercase tracking-widest text-muted-foreground">Total Pages</p>
            <p className="font-mono text-lg font-bold text-foreground">{estimate.total_pages}</p>
          </div>
          <div className="bg-card border border-border rounded-sm p-3 text-center">
            <p className="font-heading text-[9px] uppercase tracking-widest text-muted-foreground">AI Analysis</p>
            <p className="font-mono text-lg font-bold text-blue-400">{estimate.pages_needing_vision}</p>
          </div>
          <div className="bg-card border border-border rounded-sm p-3 text-center">
            <p className="font-heading text-[9px] uppercase tracking-widest text-muted-foreground">Skipped</p>
            <p className="font-mono text-lg font-bold text-muted-foreground">{estimate.pages_skipped}</p>
          </div>
          <div className="bg-card border border-amber-800/50 rounded-sm p-3 text-center">
            <p className="font-heading text-[9px] uppercase tracking-widest text-amber-500">Est. Cost</p>
            <p className="font-mono text-lg font-bold text-amber-400">${estimate.total_estimated_cost}</p>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-4 text-[10px] font-mono text-muted-foreground">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> ~{estimate.estimated_time_minutes} min</span>
          <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> Vision: ${estimate.cost_breakdown?.vision_analysis}</span>
          <span className="flex items-center gap-1"><SkipForward className="w-3 h-3" /> {estimate.pages_skipped} pages skipped (no extractable data)</span>
        </div>

        {/* Per-page breakdown */}
        <div className="bg-card border border-border rounded-sm mb-4">
          <div className="grid grid-cols-[60px_2fr_2fr_80px_80px] gap-2 px-3 py-2 border-b border-border text-[9px] font-heading uppercase tracking-widest text-muted-foreground">
            <span>Page</span><span>Sheet</span><span>Type</span><span>Cost</span><span>Extract</span>
          </div>
          <div className="max-h-[200px] overflow-auto divide-y divide-slate-800/20">
            {(estimate.per_page || []).map((p) => (
              <div key={p.page_index} className="grid grid-cols-[60px_2fr_2fr_80px_80px] gap-2 px-3 py-1.5 items-center text-[10px] font-mono" data-testid={`estimate-page-${p.page_index}`}>
                <span className="text-muted-foreground">P{p.page_index + 1}</span>
                <span className="text-foreground truncate">{p.sheet_number}</span>
                <span className={typeColors[p.page_type] || 'text-muted-foreground'}>{p.page_type}</span>
                <span className="text-muted-foreground">{p.estimated_cost > 0 ? `$${p.estimated_cost.toFixed(4)}` : '-'}</span>
                <span className={p.will_extract ? 'text-emerald-400' : 'text-muted-foreground'}>
                  {p.will_extract ? 'Yes' : 'Skip'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Confirm */}
        <button
          onClick={onConfirm}
          disabled={confirming}
          data-testid="confirm-processing-btn"
          className="w-full flex items-center justify-center gap-2 border-l-2 border-primary bg-orange-600/10 hover:bg-orange-600/20 text-primary font-heading font-bold uppercase tracking-wider text-sm h-11 px-6 transition-colors disabled:opacity-50"
        >
          {confirming ? 'Starting...' : `Start Processing ($${estimate.total_estimated_cost} estimated)`}
        </button>
      </div>
    </div>
  );
}
